import { useMemo, useState } from 'react'
import { ArrowLeft, Clock, HardDrive, Info, Play, RefreshCw, Search, X } from 'lucide-react'
import clsx from 'clsx'
import { usePlaybackStore } from '../../store'
import type { PlaylistItem } from '../../types'
import { cleanFilename, formatBytes, formatDuration, formatRelativeDate, getCompatibilityInfo } from '../../utils'
import { MediaDetailsPanel } from '../playlist/PlaylistSidebar'
import { VideoThumbnail } from '../playlist/VideoThumbnail'
import { videoApi } from '../../services/api'

interface MediaLibraryProps {
  folderName: string | null
  isOwner: boolean
  isRefreshing: boolean
  refreshError: string | null
  onBack: () => void
  onRefresh: () => void
  onPlay: (item: PlaylistItem) => void
}

export function MediaLibrary({ folderName, isOwner, isRefreshing, refreshError, onBack, onRefresh, onPlay }: MediaLibraryProps) {
  const playlist = usePlaybackStore((state) => state.playlist)
  const setPlaylist = usePlaybackStore((state) => state.setPlaylist)
  const [query, setQuery] = useState('')
  const [inspectedItem, setInspectedItem] = useState<PlaylistItem | null>(null)

  const items = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return normalized ? playlist.filter((item) => item.name.toLowerCase().includes(normalized)) : playlist
  }, [playlist, query])
  const totalSize = playlist.reduce((sum, item) => sum + item.size, 0)
  const totalDuration = playlist.reduce((sum, item) => sum + (item.durationSeconds ?? 0), 0)

  const handleHide = async (item: PlaylistItem) => {
    if (!window.confirm(`Hide “${cleanFilename(item.name)}” from this room?\n\nThe source file will stay untouched in ${item.provider === 'google' ? 'Google Drive' : 'OneDrive'}.`)) return
    await videoApi.hideFromRoom(item.id, item.provider)
    setPlaylist(playlist.filter((candidate) => candidate.id !== item.id || candidate.provider !== item.provider))
    setInspectedItem(null)
  }

  return (
    <main className="absolute inset-0 z-50 flex min-h-0 flex-col overflow-hidden bg-void/95 shadow-2xl backdrop-blur-md">
      <div className="flex flex-col gap-4 border-b border-border bg-panel px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <button onClick={onBack} className="btn-ghost rounded-lg p-2" aria-label="Back to player"><ArrowLeft size={18} /></button>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-bright">Media Library</h1>
            <p className="mt-0.5 truncate text-xs text-muted">{folderName ?? 'Room media'} · {playlist.length} videos · {formatBytes(totalSize)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-full min-w-0 sm:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} className="input py-2 pl-9 pr-9" placeholder="Search the library…" />
            {query && <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-bright"><X size={14} /></button>}
          </div>
          {isOwner && <button onClick={onRefresh} disabled={isRefreshing} className="btn-ghost rounded-lg border border-border p-2.5" title="Refresh library"><RefreshCw size={15} className={isRefreshing ? 'animate-spin' : ''} /></button>}
        </div>
      </div>

      <div className="flex items-center gap-4 border-b border-border/70 px-5 py-2.5 text-xs text-muted">
        <span className="flex items-center gap-1.5"><Clock size={13} /> {formatDuration(totalDuration)}</span>
        <span className="flex items-center gap-1.5"><HardDrive size={13} /> {formatBytes(totalSize)}</span>
        {query && <span>{items.length} matches</span>}
      </div>
      {refreshError && <p role="alert" className="mx-5 mt-3 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">{refreshError}</p>}

      <div className="flex-1 overflow-y-auto p-5">
        {items.length === 0 ? (
          <div className="grid h-48 place-items-center text-sm text-muted">{query ? 'No media matches your search.' : 'No media in this room yet.'}</div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-4">
            {items.map((item) => {
              const compatibility = getCompatibilityInfo(item)
              return (
                <article key={`${item.provider}:${item.id}`} className="group overflow-hidden rounded-xl border border-border bg-panel shadow-lg transition hover:-translate-y-0.5 hover:border-accent/40">
                  <div className="relative aspect-video overflow-hidden bg-black">
                    <VideoThumbnail item={item} large />
                    <button onClick={() => onPlay(item)} className="absolute inset-0 grid place-items-center bg-black/10 opacity-0 transition group-hover:opacity-100" aria-label={`Play ${cleanFilename(item.name)}`}>
                      <span className="grid h-11 w-11 place-items-center rounded-full bg-accent text-white shadow-xl"><Play size={18} fill="currentColor" /></span>
                    </button>
                    <span className="absolute bottom-2 right-2 rounded bg-black/80 px-1.5 py-0.5 text-[11px] font-mono text-white">{formatDuration(item.durationSeconds)}</span>
                  </div>
                  <div className="p-3">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <h2 className="truncate text-sm font-medium text-bright">{cleanFilename(item.name)}</h2>
                        <p className="mt-1 truncate text-xs text-muted">{item.provider === 'google' ? 'Google Drive' : 'OneDrive'} · {formatBytes(item.size)}</p>
                      </div>
                      <button onClick={() => setInspectedItem(item)} className="btn-ghost rounded p-1.5" aria-label={`View details for ${cleanFilename(item.name)}`}><Info size={15} /></button>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2 text-[11px] text-muted">
                      <span>{formatRelativeDate(item.modifiedAt)}</span>
                      <span className={clsx('rounded border px-1.5 py-0.5 font-semibold uppercase', compatibility.status === 'likely' ? 'border-success/35 text-success' : compatibility.status === 'warning' ? 'border-warning/40 text-warning' : 'border-danger/40 text-danger')}>{compatibility.label}</span>
                    </div>
                    <p className="mt-2 text-[11px] text-muted">{item.playCount ? `Played ${item.playCount} time${item.playCount === 1 ? '' : 's'} in this room` : 'Not played in this room'}</p>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </div>
      {inspectedItem && <MediaDetailsPanel item={inspectedItem} isOwner={isOwner} onHide={handleHide} onClose={() => setInspectedItem(null)} />}
    </main>
  )
}
